import { MIN_PASSWORD_CHARS, familyNameProblem, firstNameProblem, holdsReservedNameWord, type Appearance, type NameStepField } from "odyssey-shared";
import type { EnKey } from "odyssey-lang";
import { say } from "../words.ts";

/**
 * What the login screen shows and how it moves (P1.9-T12): the form it is on, what the player has typed, the
 * one note it shows and the field whose note line holds it, whether a request is out, and which field takes
 * focus after a move. It is pure data moved by events, so every move the screen makes is decided here and
 * unit-tested without a page; the screen draws a state and the page sends the requests and the events.
 *
 * The log in and register forms share the email and the password, so switching between them keeps what was
 * typed, and after registering the log in form holds the address just registered. The name step (P1.4-T18,
 * P1.4-T26) asks for the fields its naming grant's `needs` names, in the form's order: the family name, then
 * the first name (ADR-065 decision 3); a naming grant with no `needs`, from a gateway before families, is the
 * one-field step for the character's name as it was. The note is a sentence of the page's ("Account created,
 * now log in.") or the server's; a send clears it, since busy shows on the submit button alone, as the design
 * draws it (ADR-061's amendment "the kit and the entry forms match the design"), so the window does not grow
 * while it waits; it sits in the note line of the field it concerns, or of
 * the form's last field when it concerns no one field, and a mode switch clears it. Before a form goes out,
 * the page refuses a field that breaks the rule the gateway applies to it (`fieldProblem`) with the gateway's
 * own sentence for that field (`refusalOf`), in that field's note line, where the browser would otherwise show
 * a bubble of its own.
 *
 * The create form asks for the password twice (P1.9-T92, ADR-061's amendment "the password is typed twice to
 * create an account, and can be shown"): the confirmation is the page's alone and is never sent, the form is
 * refused at the confirm field while the two differ, and the sentence shows when that field is left or on
 * submit, never while typing. `revealed` shows the password fields as text; it returns to hidden when the form
 * is sent and whenever the screen moves on.
 */

/**
 * The form the screen is on: log in, register, or the name step (P1.4-T18, P1.4-T26).
 */
export type LoginMode = "login" | "register" | "naming";

/**
 * A field of the screen: the email and the password of the log in and register forms, the register form's
 * confirmation of the password, and the name step's family name and first name, which the wire calls
 * `characterName`, as the one-field step's character name.
 */
export type LoginField = "email" | "password" | "confirm" | "familyName" | "characterName";

/**
 * A note: its sentence, whether it reports a failure (drawn as the field's error) or not (drawn as its hint),
 * and the field whose note line holds it.
 */
export interface LoginNote {
  readonly text: string;
  readonly tone: "info" | "error";
  readonly field: LoginField;
}

/**
 * The screen's state. `needs` is the fields the name step asks for, in the form's order, or null for the
 * one-field step and on the log in and register forms. `keep` is the log in form's "Keep me logged in", which
 * a login posts (ADR-075). `confirm` is the register form's second typing of the password, which is never
 * sent, and `revealed` is whether the password fields show what they hold. `voice` is the voice picked for the
 * character the name step makes, null for none (P1.9-T83). A name step that makes a character has two steps
 * (ADR-094 decision 23): `step` is `name` for the names and `look` for the Look step, where `look` is the look
 * chosen, null until the Look step first opens and kept when a refused name returns to the names, and `lookNote` the
 * sentence the Look step shows, a refusal or a word about the voice. `focus` is the field a move asks to focus, or
 * null when it leaves focus where it is.
 */
export interface LoginState {
  readonly mode: LoginMode;
  readonly email: string;
  readonly password: string;
  readonly confirm: string;
  readonly revealed: boolean;
  readonly familyName: string;
  readonly characterName: string;
  readonly needs: readonly NameStepField[] | null;
  readonly voice: string | null;
  readonly step: "name" | "look";
  readonly look: Appearance | null;
  readonly lookNote: { readonly text: string; readonly tone: "info" | "error" } | null;
  readonly keep: boolean;
  readonly note: LoginNote | null;
  readonly busy: boolean;
  readonly focus: LoginField | null;
}

/**
 * What moves the screen:
 *
 * - `typed`: the player changed a field.
 * - `left`: the keyboard left a field.
 * - `reveal`: the player pressed a password's show or hide button.
 * - `hide`: something covered the screen, so a shown password is hidden.
 * - `kept`: the player ticked or cleared "Keep me logged in".
 * - `switched`: the mode link (Create an account, I already have an account, or the name step's Log out).
 * - `sent`: the form went out; busy shows on the submit button until the answer.
 * - `refused`: the answer was a refusal; `field` is the field the gateway named, or null for none.
 * - `invalid`: the page refused a field before sending (`fieldProblem`), with its `refusalOf` sentence.
 * - `registered`: a registration succeeded; `text` says so and `email` is the address registered.
 * - `naming`: a login was answered with a naming grant, so the account needs a name; `needs` is the fields
 *   its step asks for, absent for the one-field step.
 * - `selecting`: a login was answered with the account's characters, so character select takes the screen
 *   (P1.4-T6).
 * - `voiced`: the player picked a voice for the character the name step makes, null for none (P1.9-T83).
 * - `told`: the page has something to say that is no refusal, as that a voice has no sample to preview; it shows on
 *   the Look step.
 * - `looking`: the names passed the page's checks and the Look step opens (ADR-094 decision 23); `look` is the look
 *   it opens on the first time, kept after that.
 * - `looked`: the player changed the look on the Look step.
 * - `unlooked`: Back on the Look step returns to the names.
 * - `returned`: the name step's or character select's session is gone, or it logged out, or every session
 *   was ended; `text` is the gateway's sentence when there is one to show.
 */
export type LoginEvent =
  | { readonly type: "typed"; readonly field: LoginField; readonly value: string }
  | { readonly type: "left"; readonly field: LoginField }
  | { readonly type: "reveal" }
  | { readonly type: "hide" }
  | { readonly type: "kept"; readonly value: boolean }
  | { readonly type: "switched" }
  | { readonly type: "sent" }
  | { readonly type: "refused"; readonly text: string; readonly field: LoginField | null }
  | { readonly type: "invalid"; readonly field: LoginField }
  | { readonly type: "registered"; readonly text: string; readonly email: string }
  | { readonly type: "naming"; readonly needs?: readonly NameStepField[] }
  | { readonly type: "selecting" }
  | { readonly type: "voiced"; readonly voice: string | null }
  | { readonly type: "looking"; readonly look: Appearance }
  | { readonly type: "looked"; readonly look: Appearance }
  | { readonly type: "unlooked" }
  | { readonly type: "told"; readonly text: string }
  | { readonly type: "returned"; readonly text: string | null };

/**
 * What the page says of a field it refuses before sending, by the field: the sentence the gateway answers for the
 * same field refused (`account.refused.*`), so the check adds no copy of its own and says what the gateway would.
 * The one-field step's character name and the confirmation are the page's own (`account.fields.*`), since the
 * gateway of today answers neither.
 */
export const FIELD_REFUSAL_KEYS = {
  email: "account.refused.emailInvalid",
  password: "account.refused.passwordInvalid",
  confirm: "account.fields.confirm",
  characterName: "account.fields.characterName",
  firstName: "account.refused.firstNameInvalid",
  familyName: "account.refused.familyNameInvalid",
  reservedFamily: "account.refused.familyReserved",
} as const satisfies Readonly<Record<string, EnKey>>;

/**
 * The name step's fields, in the form's order: those its `needs` names, or the character's name alone on the
 * one-field step.
 *
 * @param state The screen's state, on the name step.
 * @returns The fields.
 */
function namingFields(state: LoginState): readonly LoginField[] {
  return state.needs ?? ["characterName"];
}

/**
 * The fields of the form on screen, in their order.
 *
 * @param state The screen's state.
 * @returns The email and the password to log in, with the confirmation to register, or the name step's
 *   fields.
 */
export function formFields(state: LoginState): readonly LoginField[] {
  if (state.mode === "naming") {
    return namingFields(state);
  }
  return state.mode === "register" ? ["email", "password", "confirm"] : ["email", "password"];
}

/**
 * Whether the confirmation holds exactly the password, character for character: nothing is trimmed or
 * folded, as the gateway keeps the password exactly as typed.
 *
 * @param state The screen's state.
 * @returns True when the two are the same string.
 */
export function passwordsMatch(state: Pick<LoginState, "password" | "confirm">): boolean {
  return state.password === state.confirm;
}

/**
 * Whether a note is the confirm field's mismatch, which typing clears once the two match.
 *
 * @param note The note shown, or null.
 * @returns True for the mismatch sentence under the confirm field.
 */
function isMismatchNote(note: LoginNote | null): boolean {
  return note !== null && note.field === "confirm" && note.text === say("account.fields.confirm");
}

/**
 * The first field of the form on screen, which opening it and a mode switch focus.
 *
 * @param state The screen's state.
 * @returns The field.
 */
export function firstField(state: LoginState): LoginField {
  return formFields(state)[0] as LoginField;
}

/**
 * The last field of the form on screen, whose note line holds a note that concerns no one field.
 *
 * @param state The screen's state.
 * @returns The field.
 */
export function lastField(state: LoginState): LoginField {
  const fields = formFields(state);
  return fields[fields.length - 1] as LoginField;
}

/**
 * The first field of the form on screen, in its order, that the page refuses before sending: an email that
 * is empty once trimmed; on registering, a password shorter than `MIN_PASSWORD_CHARS`, then a confirmation
 * that differs from it, and on logging in an empty password; on the name step, a family name or a first name that, trimmed as it is sent, the gateway would
 * refuse: out of its part's rule, or, for a family name, one the Story keeps. Logging in checks only that
 * something was typed, so the log in form teaches no password rule and every other wrong login is the
 * gateway's own "Those details do not match an account." The shape of an email is left to the gateway, which
 * answers it under the email on registering and as wrong details on logging in.
 *
 * @param state The screen's state.
 * @returns The field to refuse, or null when the form may go out.
 */
export function fieldProblem(state: LoginState): LoginField | null {
  if (state.mode === "naming") {
    for (const field of namingFields(state)) {
      const broken = field === "familyName" ? familyNameProblem(state.familyName.trim()) !== null : firstNameProblem(state.characterName.trim()) !== null;
      if (broken) {
        return field;
      }
    }
    return null;
  }
  if (state.email.trim() === "") {
    return "email";
  }
  if (state.mode === "login") {
    return state.password === "" ? "password" : null;
  }
  if (state.password.length < MIN_PASSWORD_CHARS) {
    return "password";
  }
  return passwordsMatch(state) ? null : "confirm";
}

/**
 * The sentence the page shows for a field it refuses before sending, which is the gateway's for the same
 * refusal: a family name the Story keeps is told so, and a first name asked for since families is called a
 * first name, where the one-field step's field is the character name. The confirm field's is the page's own.
 *
 * @param state The screen's state.
 * @param field The field refused.
 * @returns The sentence.
 */
export function refusalOf(state: LoginState, field: LoginField): string {
  if (field === "familyName") {
    const family = state.familyName.trim();
    if (familyNameProblem(family) !== "reserved") {
      return say("account.refused.familyNameInvalid");
    }
    return holdsReservedNameWord(family) ? say("account.refused.familyStaffWord") : say("account.refused.familyReserved");
  }
  if (field === "characterName") {
    if (firstNameProblem(state.characterName.trim()) === "reserved") {
      return say("account.refused.firstNameStaffWord");
    }
    return state.needs === null ? say("account.fields.characterName") : say("account.refused.firstNameInvalid");
  }
  return say(FIELD_REFUSAL_KEYS[field]);
}

/**
 * What the name step posts beside the refresh token: the fields its grant asks for, trimmed, or the character's
 * name alone on the one-field step, and with a character's name the voice picked for it, when one was (P1.9-T83). A
 * step that makes no character, the family step, posts no voice whatever was picked before.
 *
 * @param state The screen's state, on the name step.
 * @returns The names.
 */
export function nameStepNames(state: LoginState): { readonly familyName?: string; readonly characterName?: string; readonly voice?: string; readonly look?: Appearance } {
  const needs = state.needs ?? ["characterName"];
  return {
    ...(needs.includes("familyName") ? { familyName: state.familyName.trim() } : {}),
    ...(needs.includes("characterName") ? { characterName: state.characterName.trim(), ...(state.voice === null ? {} : { voice: state.voice }), ...(state.look === null ? {} : { look: state.look }) } : {}),
  };
}

/**
 * Whether the name step makes a character, and so has the Look step after its names (ADR-094 decision 23): the
 * one-field step and a step whose needs hold the first name; the family step alone makes none.
 *
 * @param state The screen's state.
 * @returns True on a name step that makes a character.
 */
export function makesCharacter(state: LoginState): boolean {
  return state.mode === "naming" && (state.needs === null || state.needs.includes("characterName"));
}

/**
 * The state the screen opens in: the log in form, or the name step for a tab that holds a naming grant with
 * the fields it asks for, with nothing typed and its first field asked to take focus.
 *
 * @param mode The form to open on.
 * @param needs The fields the name step asks for, absent for the one-field step.
 * @returns The state.
 */
export function initialLoginState(mode: "login" | "naming", needs?: readonly NameStepField[]): LoginState {
  const opened: LoginState = { mode, email: "", password: "", confirm: "", revealed: false, familyName: "", characterName: "", needs: mode === "naming" && needs !== undefined ? needs : null, voice: null, step: "name", look: null, lookNote: null, keep: false, note: null, busy: false, focus: null };
  return { ...opened, focus: firstField(opened) };
}

/**
 * Moves the screen by one event. While a request is out, a switch, a second send and a change to "Keep me
 * logged in" change nothing, as the page refuses them.
 *
 * - Typing changes the field; it clears the confirm field's mismatch once the two passwords match, and no
 *   other note.
 * - Leaving the confirm field of the register form while it holds something that differs from the password
 *   shows the mismatch under it, moving no focus; it changes nothing while a request is out, and it leaves
 *   an error under another field in place, since the submit still refuses the mismatch.
 * - The reveal shows the password fields or hides them again, and changes nothing while a request is out.
 *   Hiding hides them and never shows them. Every move below but a refusal before sending hides them.
 * - A switch goes from log in to register and back, and from the name step to log in; it clears the note
 *   and focuses the new form's first field.
 * - A send clears the note and waits; busy shows on the submit button alone.
 * - A refusal ends the wait and shows the sentence as an error in the note line of the field it names when
 *   that field is on the form, or of the last field, and focuses that field, where the player acts next. On the
 *   Look step a refusal that names a field of the form returns to the names with the look kept; any other stays on
 *   the Look step, its sentence over the rows. A
 *   refused login also clears the password, as every login answer does, and the confirmation with it
 *   wherever the password is cleared.
 * - A field refused before sending shows its `refusalOf` sentence as its error and takes focus, keeping
 *   everything typed; while a request is out it changes nothing, as the page sends nothing then.
 * - A registration returns to log in with the email filled with the address registered, the password
 *   cleared and focused, and the sentence under it.
 * - A naming grant moves to the name step for the fields it asks for, with the password cleared, no voice picked
 *   and the step's first field focused.
 * - A voice picked is kept and clears a Look step's note that was no error; something told shows on the Look
 *   step; while a request is out each changes nothing.
 * - The Look step opens only on a step that makes a character, on the look it had or the one given; a change of
 *   look clears its note; Back returns to the names and focuses the last field.
 * - Character select taking the screen ends the wait with the password cleared, the note gone and no field
 *   to focus, since the log in form is not shown until a return.
 * - A return goes to log in with the gateway's sentence, if any, as an error under the password, and
 *   focuses the email.
 *
 * @param state The state.
 * @param event What happened.
 * @returns The next state.
 */
export function loginReducer(state: LoginState, event: LoginEvent): LoginState {
  switch (event.type) {
    case "typed": {
      const typed: LoginState = { ...state, [event.field]: event.value, focus: null };
      return isMismatchNote(typed.note) && passwordsMatch(typed) ? { ...typed, note: null } : typed;
    }
    case "left": {
      const elsewhere = state.note !== null && state.note.tone === "error" && state.note.field !== "confirm";
      if (event.field !== "confirm" || state.mode !== "register" || state.busy || elsewhere || state.confirm === "" || passwordsMatch(state)) {
        return state;
      }
      return { ...state, note: { text: say("account.fields.confirm"), tone: "error", field: "confirm" }, focus: null };
    }
    case "reveal":
      return state.busy ? state : { ...state, revealed: !state.revealed, focus: null };
    case "hide":
      return state.revealed ? { ...state, revealed: false } : state;
    case "kept":
      return state.busy ? state : { ...state, keep: event.value, focus: null };
    case "switched": {
      if (state.busy) {
        return state;
      }
      const mode: LoginMode = state.mode === "login" ? "register" : "login";
      return { ...state, mode, needs: null, revealed: false, note: null, step: "name", look: null, lookNote: null, focus: "email" };
    }
    case "sent":
      if (state.busy) {
        return state;
      }
      return { ...state, busy: true, revealed: false, note: null, lookNote: null, focus: null };
    case "refused": {
      if (state.step === "look" && (event.field === null || !formFields(state).includes(event.field))) {
        return { ...state, busy: false, lookNote: { text: event.text, tone: "error" }, focus: null };
      }
      const field = event.field !== null && formFields(state).includes(event.field) ? event.field : lastField(state);
      const cleared = state.mode === "login";
      return { ...state, busy: false, step: "name", password: cleared ? "" : state.password, confirm: cleared ? "" : state.confirm, note: { text: event.text, tone: "error", field }, focus: field };
    }
    case "invalid":
      if (state.busy) {
        return state;
      }
      return { ...state, note: { text: refusalOf(state, event.field), tone: "error", field: event.field }, focus: event.field };
    case "registered":
      return { ...state, mode: "login", email: event.email, password: "", confirm: "", revealed: false, busy: false, note: { text: event.text, tone: "info", field: "password" }, focus: "password" };
    case "naming": {
      const naming: LoginState = { ...state, mode: "naming", needs: event.needs ?? null, voice: null, step: "name", look: null, lookNote: null, password: "", confirm: "", revealed: false, busy: false, note: null };
      return { ...naming, focus: firstField(naming) };
    }
    case "selecting":
      return { ...state, password: "", confirm: "", revealed: false, busy: false, note: null, focus: null };
    case "voiced":
      return state.busy ? state : { ...state, voice: event.voice, lookNote: state.lookNote?.tone === "error" ? state.lookNote : null, focus: null };
    case "told":
      return state.busy ? state : { ...state, lookNote: { text: event.text, tone: "info" }, focus: null };
    case "looking":
      if (state.busy || !makesCharacter(state)) {
        return state;
      }
      return { ...state, step: "look", look: state.look ?? event.look, note: null, lookNote: null, focus: null };
    case "looked":
      return state.busy || state.step !== "look" ? state : { ...state, look: event.look, lookNote: null, focus: null };
    case "unlooked":
      return state.busy || state.step !== "look" ? state : { ...state, step: "name", lookNote: null, focus: lastField(state) };
    case "returned":
      return { ...state, mode: "login", needs: null, step: "name", look: null, lookNote: null, confirm: "", revealed: false, busy: false, note: event.text === null ? null : { text: event.text, tone: "error", field: "password" }, focus: "email" };
  }
}
